import React, { useState, useEffect } from 'react'

import { Card, Stat, Notice } from './ui'
import PositionImpact from './PositionImpact'
import PositionWeekGrid from './PositionWeekGrid'
import WeekScrubber from './WeekScrubber'
import LineupTable from './LineupTable'
import {
  signedPoints,
  signedPercent,
  percent,
  points,
  adp,
  deltaClass,
  formatLabel,
} from '../lib/format'

// The draft-cost half of the summary card: what each side's players cost on
// this season's draft board. It sits inside "Per week, on average" rather than
// in a panel of its own because it answers the same question the stats above
// it do — was this a fair deal for this team — and as a separate card it sat
// below three charts, too far away to read against them.
//
// Received and given stack rather than sitting side by side: the summary card
// is a third of the row on a wide screen, and two columns of player names
// truncate to nothing at that width.
function DraftCost({ adpForTeam, phantomIds, teamName }) {
  if (!adpForTeam) return null

  const { received = [], given = [], delta, format } = adpForTeam

  return (
    <div className="mt-4 border-t pt-3 border-slate-700">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-300">
        Draft cost
      </div>
      <p className="mt-0.5 text-xs text-slate-500">
        Average draft position on the {formatLabel(format)} board
      </p>

      <div className="mt-2 space-y-3">
        <AdpList label="Received" entries={received} />
        <AdpList label="Given up" entries={given} phantomIds={phantomIds} />
      </div>

      <p className="mt-3 text-sm text-slate-300">
        {delta === null || delta === undefined ? (
          'Not enough draft data to compare — at least one player went undrafted.'
        ) : delta > 0 ? (
          <>
            {/* Named rather than "you": this panel is shown for whichever tab is
                open, which in a multi-team trade is usually not your own team,
                and on a shared link is never anybody in particular. */}
            {teamName} is acquiring players drafted{' '}
            <strong className="tabular-nums">{Math.abs(delta).toFixed(1)}</strong> picks
            earlier than the ones it gives up.
          </>
        ) : (
          <>
            {teamName} is giving up players drafted{' '}
            <strong className="tabular-nums">{Math.abs(delta).toFixed(1)}</strong> picks
            earlier than the ones it receives.
          </>
        )}
      </p>
    </div>
  )
}

// phantomIds is only ever passed for the outgoing side — a team cannot receive
// a player it already fails to roster.
function AdpList({ label, entries, phantomIds }) {
  return (
    <div>
      <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">
        {label}
      </h4>
      <ul className="space-y-1 text-sm">
        {entries.map((entry) => (
          <li key={entry.player_id} className="flex justify-between gap-2">
            <span className="truncate text-slate-200">
              {entry.name}
              <span className="ml-1 text-xs text-slate-400">{entry.position}</span>
              {phantomIds?.has(entry.player_id) && (
                <span className="ml-1 text-xs italic text-slate-400">
                  not on this roster
                </span>
              )}
            </span>
            <span className="tabular-nums text-slate-300">
              {adp(entry.adp)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function WeekDetail({ weekResult, teamId, incomingIds }) {
  const view = weekResult?.teams?.[teamId]
  if (!view) return null

  const before = view.before || {}
  const after = view.after || {}
  const hasOpponent = view.opponent_id

  return (
    <Card
      title={`Week ${weekResult.week}`}
      subtitle={
        hasOpponent
          ? `vs ${view.opponent_name} — projected ${points(view.opponent_points)}`
          : 'No opponent scheduled this week'
      }
    >
      {hasOpponent && (
        <div className="mb-4 grid grid-cols-2 gap-4 sm:flex sm:flex-wrap sm:items-center sm:gap-6">
          <Stat label="Win % before" value={percent(before.win_probability)} />
          <Stat label="Win % after" value={percent(after.win_probability)} />
          <Stat
            label="Change"
            value={signedPercent(view.delta?.win_probability)}
            tone={deltaClass(view.delta?.win_probability)}
          />
          <Stat
            label="Points"
            value={signedPoints(view.delta?.points)}
            tone={deltaClass(view.delta?.points)}
          />
        </div>
      )}

      <div className="grid gap-6 sm:grid-cols-2">
        <LineupTable label="Before" lineup={before.lineup} total={before.points} />
        <LineupTable
          label="After"
          lineup={after.lineup}
          total={after.points}
          highlightIds={incomingIds}
        />
      </div>

      <p className="mt-3 text-xs text-slate-500">
        Both lineups are the best legal lineup for the week, opponents included.
      </p>
    </Card>
  )
}

// Everything about an evaluation that reads the same whether you own the trade
// or opened a shared link: the team toggle, the charts, the week detail and the
// draft cost. Owning the teamId/week state here is what lets both callers stay
// thin — they differ only in the chrome above this, and in whether the payload
// they hand over has been redacted.
//
// `trade` is the trade object itself, not a query result. `preferredTeamId`
// only nudges the default tab; a shared view passes nothing and lands on the
// first side of the deal.
export default function TradeEvaluationView({ trade, results, preferredTeamId = null }) {
  const [teamId, setTeamId] = useState(null)
  const [week, setWeek] = useState(null)

  const teams = results?.teams || []
  const weeks = results?.weeks || []

  useEffect(() => {
    if (teamId || teams.length === 0) return
    const preferred = teams.find((t) => String(t.id) === String(preferredTeamId)) || teams[0]
    setTeamId(preferred.id)
  }, [teams, preferredTeamId, teamId])

  useEffect(() => {
    if (week || weeks.length === 0) return
    setWeek(weeks[0].week)
  }, [weeks, week])

  if (weeks.length === 0) {
    return (
      <Card title="Nothing left to evaluate">
        <p className="text-sm text-slate-300">
          {results?.meta?.note || 'The regular season is over.'}
        </p>
      </Card>
    )
  }

  const summary = results.summary?.[teamId] || {}
  const weekResult = weeks.find((w) => w.week === week) || weeks[0]
  const deltasByWeek = Object.fromEntries(
    weeks.map((w) => [w.week, w.teams?.[teamId]?.delta?.win_probability])
  )

  // Which weeks got better, not how many — same rule the summary counts with:
  // a positive points delta.
  const improvedWeeks = weeks
    .filter((w) => (w.teams?.[teamId]?.delta?.points ?? 0) > 0)
    .map((w) => w.week)

  const assets = trade?.assets || []

  const incomingIds = assets
    .filter((asset) => String(asset.to_team_id) === String(teamId))
    .map((asset) => asset.player_id)

  const exploratory = Boolean(trade?.exploratory)
  const teamName = teams.find((t) => String(t.id) === String(teamId))?.name || 'This team'

  // Players this side nominally gives up but does not actually roster. Only an
  // exploratory trade can produce them, and they cost it nothing — the lineup
  // it loses them from never held them. Saying so is the difference between a
  // sandbox and a bug.
  const phantomOutgoing = assets.filter(
    (asset) =>
      String(asset.from_team_id) === String(teamId) &&
      String(asset.owner_team_id ?? '') !== String(teamId)
  )
  const phantomIds = new Set(phantomOutgoing.map((asset) => asset.player_id))

  return (
    <>
      {/* Two teams split the bar into equal centered segments — otherwise the
          names hug the left edge of a wide bar. Past two, equal segments on a
          phone leave ~80px each and every name truncates to nothing, so the bar
          scrolls instead and each tab keeps its full width. */}
      <div
        className={`mb-4 flex w-full rounded-md border border-slate-600 ${
          teams.length > 2 ? 'overflow-x-auto' : ''
        }`}
      >
        {teams.map((team) => (
          <button
            key={team.id}
            type="button"
            onClick={() => setTeamId(team.id)}
            className={`min-w-0 truncate px-3 py-2 text-center text-sm first:rounded-l-md last:rounded-r-md ${
              teams.length > 2 ? 'shrink-0' : 'flex-1'
            } ${
              String(team.id) === String(teamId)
                ? 'bg-slate-100 text-slate-900'
                : 'text-slate-300'
            }`}
          >
            {team.name}
          </button>
        ))}
      </div>

      {results.meta?.projections_present === false && (
        <div className="mb-4">
          <Notice title="No projections were available for these weeks">
            Every point total and win probability below is zero because the
            projection data is missing — not because the trade is neutral. Run{' '}
            <code className="font-mono">bin/rails sleeper:bootstrap</code>, then
            reload.
          </Notice>
        </div>
      )}

      {exploratory && phantomOutgoing.length > 0 && (
        <div className="mb-4">
          <Notice title={`${teamName} doesn’t roster everyone on its side`}>
            {phantomOutgoing.map((asset) => asset.player_name).join(', ')}{' '}
            {phantomOutgoing.length === 1 ? 'belongs' : 'belong'} to another team,
            so this lineup loses nothing for them — while the side receiving them
            still gains them. The sides will not add up, which is what an
            exploration is for.
          </Notice>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3 lg:items-start">
        <Card title="Per week, on average" className="lg:col-span-1">
          <div className="grid grid-cols-2 gap-4">
            <Stat
              label="Points"
              value={signedPoints(summary.points_per_week)}
              tone={deltaClass(summary.points_per_week)}
            />
            <Stat
              label="Win odds"
              value={signedPercent(summary.win_probability_per_week)}
              tone={deltaClass(summary.win_probability_per_week)}
            />
            <div className="col-span-2">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
                Weeks better
              </div>
              {improvedWeeks.length === 0 ? (
                <div className="mt-0.5 text-xl font-semibold">None</div>
              ) : (
                <div className="mt-1 flex flex-wrap gap-1">
                  {improvedWeeks.map((week) => (
                    <button
                      key={week}
                      type="button"
                      onClick={() => setWeek(week)}
                      className="rounded-md px-1.5 py-1 text-xs font-semibold tabular-nums border border-slate-700 bg-slate-900 hover:border-slate-500"
                    >
                      Wk {week}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DraftCost
            adpForTeam={results.adp?.[teamId]}
            phantomIds={phantomIds}
            teamName={teamName}
          />
        </Card>

        <Card title="By roster position" subtitle="Season average, per week" className="lg:col-span-2">
          <PositionImpact
            byPosition={summary.points_by_position}
            net={summary.points_per_week}
          />
        </Card>
      </div>

      <div className="mt-4">
        <Card
          title="Week by week"
          subtitle="Change in win probability for each remaining matchup. Pick a week to load it below."
        >
          <WeekScrubber
            weeks={weeks.map((w) => w.week)}
            selected={weekResult.week}
            onSelect={setWeek}
            deltasByWeek={deltasByWeek}
          />
        </Card>
      </div>

      <div className="mt-4">
        <Card
          title="Position impact, week by week"
          subtitle="Points gained or lost at each roster position. Bars run up for a gain, down for a loss, and every row shares one scale."
        >
          <PositionWeekGrid
            weeks={weeks}
            teamId={teamId}
            averages={summary.points_by_position}
            selectedWeek={weekResult.week}
            onSelectWeek={setWeek}
          />
        </Card>
      </div>

      <div className="mt-4">
        <WeekDetail weekResult={weekResult} teamId={teamId} incomingIds={incomingIds} />
      </div>

      <p className="mt-6 text-xs text-slate-500">
        Projections from {results.meta?.projection_source}, scored under this league's own rules.
        Win probability is computed here — no fantasy platform publishes one.
        {results.meta?.variance_calibrated
          ? ` Spread calibrated on ${results.meta.variance_samples} historical results.`
          : ' Spread uses positional priors until this season has results to learn from.'}
      </p>
    </>
  )
}
